import { useCallback, useMemo, useState, type MouseEvent } from 'react'
import type { ChartData, ChartOptions } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { IPlatformDashboardDemographicCompletenessRow } from '@brushfire/types'
import '@/features/campaigns/charts/registerCharts'
import {
  getDemographicBarColor,
  PLATFORM_CHART_CARD,
} from '../lib/platform-dashboard-layout'
import { DonutHoverPill } from './DonutHoverPill'

const chart = PLATFORM_CHART_CARD.demographic

const xAxisFont = { family: 'Inter, sans-serif', size: chart.axisFontSize }
const yAxisFont = { family: 'Inter, sans-serif', size: chart.yAxisLabelFontSize }

interface Props {
  rows: IPlatformDashboardDemographicCompletenessRow[]
}

export function DemographicCompletenessChart({ rows }: Props) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null)

  const barColors = useMemo(
    () => rows.map((r) => getDemographicBarColor(r.completionPercent)),
    [rows],
  )

  const handleHover = useCallback(
    (_event: unknown, elements: { index: number }[]) => {
      setHoveredIndex(elements.length > 0 ? elements[0].index : null)
    },
    [],
  )

  const handleMouseMove = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    setCursor({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    })
  }, [])

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null)
    setCursor(null)
  }, [])

  const data = useMemo<ChartData<'bar'>>(
    () => ({
      labels: rows.map((r) => r.label),
      datasets: [
        {
          label: 'Completion %',
          data: rows.map((r) => r.completionPercent),
          backgroundColor: barColors,
          borderRadius: {
            topRight: chart.barRadius,
            bottomRight: chart.barRadius,
          },
          borderSkipped: 'start',
          barThickness: chart.barThickness,
        },
      ],
    }),
    [barColors, rows],
  )

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      onHover: handleHover,
      layout: { padding: { left: 0, right: 4, top: 0, bottom: 0 } },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
      scales: {
        x: {
          min: 0,
          max: 100,
          grid: {
            color: chart.grid,
            borderDash: [3, 3],
            drawBorder: false,
            tickLength: 0,
          },
          ticks: {
            stepSize: 10,
            color: chart.axisLabel,
            font: xAxisFont,
            padding: 6,
            callback: (value) => String(value),
          },
        },
        y: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: chart.axisLabel,
            font: yAxisFont,
            autoSkip: false,
            padding: 8,
          },
        },
      },
    }),
    [handleHover],
  )

  const hoveredRow = hoveredIndex === null ? null : rows[hoveredIndex] ?? null

  return (
    <Box
      data-testid="platform-demographic-completeness-chart"
      sx={{
        border: chart.border,
        borderRadius: chart.borderRadius,
        boxShadow: chart.boxShadow,
        pt: chart.paddingTop,
        pb: chart.paddingBottom,
        pl: chart.paddingLeft,
        pr: chart.paddingRight,
        bgcolor: chart.bgcolor,
        height: chart.height,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        sx={{
          fontSize: chart.titleFontSize,
          fontWeight: chart.titleFontWeight,
          fontFamily: chart.titleFontFamily,
          color: chart.titleColor,
          letterSpacing: chart.titleLetterSpacing,
          lineHeight: chart.titleLineHeight,
          mb: chart.titleToBodyGap,
        }}
      >
        Demographic Profile Completeness
      </Typography>
      <Box
        sx={{ flex: 1, minHeight: 0, position: 'relative' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {rows.length === 0 ? (
          <Typography sx={{ fontSize: '12px', fontFamily: 'Inter, sans-serif', color: chart.axisLabel }}>
            No data
          </Typography>
        ) : (
          <>
            <Bar data={data} options={options} />
            {hoveredRow && cursor ? (
              <DonutHoverPill
                testId="platform-demographic-hover-pill"
                label={hoveredRow.label}
                value={`${hoveredRow.completionPercent}%`}
                borderColor={chart.hoverPillBorder}
                left={cursor.x}
                top={cursor.y}
                height={chart.hoverPillHeight}
                borderRadius={chart.hoverPillRadius}
              />
            ) : null}
          </>
        )}
      </Box>
    </Box>
  )
}
